import React, { useMemo, useState } from 'react';

import {
  BarChart2,
  Users,
  Search,
  FileText,
  Send,
  ChevronDown,
  Info,
  Globe,
  MapPin,
  Flag,
  ListChecks,
  Receipt,
  UserRound,
} from 'lucide-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';

import { getAdminId, getPermissions, getRoles } from '../../utils/tokenUtils';
import SimpleAlert from '../common/SimpleAlert';

import 'react-perfect-scrollbar/dist/css/styles.css';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  route?: string;
  requiredPermission?: string;
  children?: MenuItem[]; // Sub-items
}

interface MenuGroup {
  group: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, activePage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const roles = getRoles() || [];
  const permissions = getPermissions() || [];
  const navigate = useNavigate();
  const adminId = getAdminId();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleNavigation = (page: string): void => {
    navigate(page);
  };

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    );
  };

  const renderMenuItems = (items: MenuItem[]) => {
    const isRouteActive = (route: string) => {
      // Return false if either path is missing
      if (!activePage || !route) {
        return false;
      }

      const currentPath = activePage.toLowerCase();
      const menuRoute = route.toLowerCase();

      // Check if we're on the exact route
      if (currentPath === menuRoute) {
        return true;
      }

      // Check if we're on a sub-route (detail page, create page, etc.)
      try {
        return currentPath.startsWith(`${menuRoute}/`);
      } catch {
        // If something goes wrong, fall back to simple comparison
        return false;
      }
    };

    return items.map((item, idx) => {
      const isParentActive = item.children
        ? item.children.some(
            (child) => child.route && isRouteActive(child.route),
          )
        : item.route && isRouteActive(item.route);

      const isExpanded = expandedItems.includes(item.label) || isParentActive;

      return (
        <li key={idx}>
          <a
            onClick={() => {
              if (item.children) {
                toggleExpand(item.label);
              } else if (item.route) {
                handleNavigation(item.route);
              }
            }}
            className={`flex cursor-pointer items-center gap-3 rounded-md p-3 text-sm font-semibold transition-colors ${
              isParentActive
                ? 'bg-primary text-secondary'
                : 'text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          >
            {item.icon}
            <span className="flex-1">{item.label}</span>
            {item.children && (
              <ChevronDown
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            )}
          </a>
          {item.children && (
            <ul
              className={`relative ml-6 overflow-hidden transition-all duration-200 ease-in-out ${
                isExpanded ? 'max-h-96 mt-1' : 'max-h-0'
              }`}
            >
              <div className="absolute bottom-0 top-0 w-px bg-white/15" />
              {item.children.map((child, childIdx) => (
                <li key={`${idx}-${childIdx}`} className="relative">
                  <div className="absolute left-0 top-1/2 h-px w-4 bg-white/15" />
                  <a
                    onClick={() => child.route && handleNavigation(child.route)}
                    className={`flex cursor-pointer items-center gap-3 rounded-md py-2 pl-6 text-sm transition-colors ${
                      child.route && isRouteActive(child.route)
                        ? 'font-semibold text-primary'
                        : 'text-white/50 hover:text-white'
                    }`}
                  >
                    <span>{child.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  };

  const menuGroups: MenuGroup[] = [
    // Members (role "User") get no permission claims at all, so unlike the Admin groups below,
    // these items carry no requiredPermission and always show for that role. The one exception is
    // the member dashboard: a plain member does not get it (the user's call, 2026-10-19), their
    // home is the profile. It needs the admin dashboard permission, so only an admin who is also a
    // member sees it. An account can hold both roles (e.g. the owner is an Admin who is also a
    // member), in which case the member group sits beside the admin ones under its own heading.
    ...(roles.includes('User')
      ? [
          {
            group: roles.includes('Admin') ? 'My Golf' : 'Main',
            items: [
              {
                label: 'My Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
              {
                label: 'My Profile',
                icon: <UserRound className="w-5 h-5" />,
                route: '/member/profile',
              },
              {
                label: 'Home Courses',
                icon: <MapPin className="w-5 h-5" />,
                route: '/member/home-courses',
              },
              {
                label: 'Courses Played',
                icon: <Flag className="w-5 h-5" />,
                route: '/member/courses-played',
              },
              {
                label: 'Bucket List',
                icon: <ListChecks className="w-5 h-5" />,
                route: '/member/bucket-list',
              },
            ],
          },
        ]
      : []),
    ...(roles.includes('Admin')
      ? [
          {
            group: 'Main',
            items: [
              {
                label: 'Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/admin/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
            ],
          },
          {
            group: 'Communication',
            items: [
              {
                label: 'Bulk Email',
                icon: <Send className="w-5 h-5" />,
                route: '/admin/bulkemails',
                requiredPermission: 'Communication.BulkEmails.View',
              },
            ],
          },
          {
            group: 'Member',
            items: [
              {
                label: 'Members',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/members',
                requiredPermission: 'Member.Members.View',
              },
            ],
          },
          {
            group: 'Settings',
            items: [
              {
                label: 'Audit Logs',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/auditlogs',
                requiredPermission: 'Settings.AuditLog.View',
              },
              {
                label: 'Company Info',
                icon: <Info className="w-5 h-5" />,
                route: '/admin/companyinfo',
                requiredPermission: 'Settings.CompanyInfo.View',
              },
              {
                label: 'Countries',
                icon: <Globe className="w-5 h-5" />,
                route: '/admin/countries',
                requiredPermission: 'Settings.Country.View',
              },
              {
                label: 'Pins',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/pins',
                requiredPermission: 'Settings.Pin.View',
              },
              {
                label: 'Users',
                icon: <Users className="w-5 h-5" />,
                route: '/admin/users',
                requiredPermission: 'Settings.Administrators.View',
              },

              {
                label: 'Courses',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/courses',
                requiredPermission: 'Settings.Course.View',
              },
              {
                label: 'Course Requests',
                icon: <Flag className="w-5 h-5" />,
                route: '/admin/course-requests',
                requiredPermission: 'Settings.Course.View',
              },

              {
                label: 'Member Types',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/membertypes',
                requiredPermission: 'Settings.MemberType.View',
              },

              {
                label: 'Invoices',
                icon: <Receipt className="w-5 h-5" />,
                route: '/admin/invoices',
                requiredPermission: 'Settings.Invoice.View',
              },
            ],
          },
        ]
      : []),
  ];

  const filteredGroups = useMemo(
    () =>
      menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (!item.requiredPermission ||
                permissions.includes(item.requiredPermission)) &&
              item.label.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [menuGroups, permissions, searchTerm],
  );

  return (
    <aside
      id="nav-menu"
      aria-label="Side navigation"
      // Below lg it is pinned under AdminLayout's two sticky bars (8rem) by an explicit top, not by
      // where it happens to fall in the flow, because the page itself scrolls there.
      className={`fixed top-32 lg:static z-40 w-72 bg-secondary border-r border-primary/25 h-[calc(100%-8rem)] lg:h-full transition-transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 flex flex-col`}
    >
      {/* Search Box */}
      <div className="relative my-6 px-4">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search here"
          aria-label="Search content"
          className="relative h-10 w-full rounded-md border border-white/15 bg-white/5 px-4 pr-12 text-sm text-white outline-none transition-colors
            placeholder:text-white/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30
            disabled:cursor-not-allowed disabled:bg-white/5 disabled:text-white/30"
        />
        <Search
          className="absolute right-7 top-2.5 h-5 w-5 cursor-pointer stroke-white/50"
          aria-hidden="true"
          aria-label="Search icon"
          role="graphics-symbol"
        />
      </div>

      {/* Navigation Links */}
      <PerfectScrollbar
        options={{ suppressScrollX: true }}
        className="flex-1 overflow-hidden"
      >
        <div className="">
          {filteredGroups.map((group, index) => (
            <div key={index} className="mb-4">
              <h2 className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-primary/80">
                {group.group}
              </h2>
              <ul className="space-y-2 px-4">{renderMenuItems(group.items)}</ul>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
      {adminId && (
        <SimpleAlert message="Logged in as member" variant="success" />
      )}

      {/* Footer */}
      <footer className="mt-auto border-t border-white/10 p-4">
        <p className="text-xs text-white/40">
          &copy; {new Date().getFullYear()} GolfPlayed. All rights reserved.
          Developed by Latreia.
        </p>
      </footer>
    </aside>
  );
};

export default Sidebar;
